import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Stack,
  Checkbox,
  ListItemText,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Search, UploadIcon, UserPlus, X } from "lucide-react";
import AddIcon from "@mui/icons-material/Add";
import { axiosCall } from "../utils/axiosCall";
import constants from "../config/constants";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { EditIcon } from "./CustomIcons";
import { getStateApi } from "../services/state.service";
import { useGetApi } from "../hooks/useGetApi";
import { getCityApi } from "../services/city.service";
import { getRelationshipApi } from "../services/relationship.service";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const EMPTY_EDIT_STATE = {
  title: false,
  firstName: false,
  lastName: false,
  phone: false,
  dob: false,
  gender: false,
  address: false,
  city: false,
  state: false,
  pinCode: false,
  emergencyContactName: false,
  emergencyContactRelationship: false,
  emergencyContactNumber: false,
  insuranceProvider: false,
  policyNumber: false,
  coverage: false,
  patientImage: false,
};

const validateInsuranceFields = (info) => {
  if (info?.insuranceProvider?.trim() && !info?.policyNumber?.trim()) {
    return "Policy number is required when insurance provider is selected.";
  }
  return null;
};

function Information() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { patientId } = useParams();

  const [patientInfo, setPatientInfo] = React.useState({
    UHID: "",
    firstName: "",
    lastName: "",
    phone: "",
    dob: "",
    gender: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactNumber: "",
    insuranceProvider: "",
    policyNumber: "",
    coverage: "",
    patientImage: "",
  });

  const [edit, setEdit] = useState({ ...EMPTY_EDIT_STATE });
  const [isSaving, setIsSaving] = useState(false);
  /** Insurance values from server when user opened insurance edit (for cancel ×). */
  const insuranceEditBaselineRef = useRef(null);

  useEffect(() => {
    if (patientId) {
      fetchPatient(patientId);
    }
  }, [patientId]);

  const formData = new FormData();

  const handleUpload = async (e) => {
    formData.append("type", "patient");
    formData.append("image", e.target.files[0]);
    setEdit({
      ...edit,
      patientImage: true,
    });

    const uploadPic = await axiosCall(
      constants.POST_METHOD,
      constants.UPLOAD_IMAGE,
      formData,
      null,
      user.token
    );

    if (uploadPic.isSuccess) {
      setPatientInfo({
        ...patientInfo,
        patientImage: uploadPic.data?.data?.Location,
      });
    } else {
      toast.error(uploadPic.error?.message || "Image upload failed.");
    }
  };

  const fetchPatient = async () => {
    try {
      const getPatient = await axiosCall(
        constants.GET_METHOD,
        constants.GET_PATIENT + `/${patientId}`,
        null,
        null,
        user.token
      );

      if (getPatient.isSuccess) {
        console.log(getPatient.data.data);
        setPatientInfo({
          ...getPatient.data.data,
          city: getPatient.data.data.city._id,
          state: getPatient.data.data.state._id,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const exitInsuranceEditMode = () => {
    setEdit((prev) => ({
      ...prev,
      insuranceProvider: false,
      policyNumber: false,
      coverage: false,
    }));
  };

  const persistPatientUpdate = async (
    payload,
    successMessage = "Patient updated successfully!"
  ) => {
    setIsSaving(true);
    try {
      const response = await axiosCall(
        constants.PUT_METHOD,
        constants.GET_PATIENT + `/${patientId}`,
        payload,
        null,
        user.token
      );

      if (response.isSuccess) {
        toast.success(successMessage);
        await fetchPatient();
        return true;
      }

      toast.error(response.error?.message || "Update failed. Please try again.");
      return false;
    } catch (error) {
      console.log(error);
      toast.error("An error occurred while updating the patient.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const updatePatient = async () => {
    const insuranceError = validateInsuranceFields(patientInfo);
    if (insuranceError) {
      toast.error(insuranceError);
      return;
    }

    const ok = await persistPatientUpdate(patientInfo);
    if (ok) {
      insuranceEditBaselineRef.current = null;
      setEdit({ ...EMPTY_EDIT_STATE });
    }
  };

  /** Cancel insurance edit — revert to server values; no save, no toast. */
  const cancelInsuranceEdit = () => {
    const baseline = insuranceEditBaselineRef.current ?? {
      insuranceProvider: "",
      policyNumber: "",
      coverage: "",
    };
    setPatientInfo((prev) => ({
      ...prev,
      insuranceProvider: baseline.insuranceProvider ?? "",
      policyNumber: baseline.policyNumber ?? "",
      coverage: baseline.coverage ?? "",
    }));
    exitInsuranceEditMode();
    insuranceEditBaselineRef.current = null;
  };

  const handleInputChange = (e) => {
    setPatientInfo({
      ...patientInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditClick = (name) => {
    if (
      name === "insuranceProvider" ||
      name === "policyNumber" ||
      name === "coverage"
    ) {
      insuranceEditBaselineRef.current = {
        insuranceProvider: patientInfo.insuranceProvider ?? "",
        policyNumber: patientInfo.policyNumber ?? "",
        coverage: patientInfo.coverage ?? "",
      };
      setEdit((prev) => ({
        ...prev,
        insuranceProvider: true,
        policyNumber: true,
        coverage: true,
      }));
      return;
    }
    setEdit((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const fileInputRef = useRef(null);
  const fileInputEditRef = useRef(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadEditClick = () => {
    if (fileInputEditRef.current) {
      fileInputEditRef.current.click();
    }
  };
  const { dataList: stateList } = useGetApi({
    apiFunction: getStateApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "states",
  });
  const { dataList: cityList } = useGetApi({
    apiFunction: getCityApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "cities",
  });
  const { dataList: relationshipList } = useGetApi({
    apiFunction: getRelationshipApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "relationships",
  });

  return (
    <Box sx={{ width: "100%" }}>
      {/* Form */}

      <Box
        sx={{
          padding: 2,
          marginTop: 2,
          borderRadius: 2,
        }}
      >
        {/* Patient Information */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: 2,
            marginBottom: 2,
            display: "flex",
            gap: 4,
          }}
        >
          <Box
            sx={{
              width: "25rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-evenly",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "3.5rem",
                background: "#92a6cc",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                padding: "0 8px",
                boxSizing: "border-box",
              }}
            >
              <Typography 
                color={"#fff"}
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                  wordBreak: "keep-all",
                  whiteSpace: "nowrap",
                }}
              >
                UHID: {patientInfo.UHID}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "15rem",
                border: "1px solid #5d6a82",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {/* TODO: Image */}
              {patientInfo.patientImage && (
                <>
                  <img
                    src={patientInfo.patientImage}
                    alt="Patient"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Button
                    variant=""
                    sx={{
                      color: "#5d6a82",
                      background: "#dfe8ef",
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                    }}
                    onClick={handleUploadEditClick}
                  >
                    Edit
                  </Button>
                  <input
                    type="file"
                    ref={fileInputEditRef}
                    style={{ display: "none" }}
                    onChange={(e) => handleUpload(e)}
                    inputProps={{ accept: "image/*" }}
                  />
                </>
              )}
            </Box>
            {!patientInfo.patientImage && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => handleUpload(e)}
                  inputProps={{ accept: "image/*" }}
                />
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    width: "100%",
                    height: "3rem",
                    color: "#0179B0",
                    textTransform: "none",
                  }}
                  onClick={handleUploadClick}
                >
                  Upload
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* Basic Information */}

            <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1" marginBottom={2} color="grey">
                Basic Information
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 1, flex: "1 1 30%" }}>
                  <FormControl sx={{ minWidth: 80, position: "relative" }}>
                    <InputLabel
                      sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                    >
                      Title
                    </InputLabel>
                    <Select
                      label="Title"
                      value={patientInfo.title || ""}
                      onChange={handleInputChange}
                      name="title"
                      disabled={!edit.title}
                      variant="outlined"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Mr.">Mr.</MenuItem>
                      <MenuItem value="Mrs.">Mrs.</MenuItem>
                      <MenuItem value="Dr.">Dr.</MenuItem>
                      <MenuItem value="Ms.">Ms.</MenuItem>
                      <MenuItem value="Prof.">Prof.</MenuItem>
                      <MenuItem value="Er.">Er.</MenuItem>
                      <MenuItem value="Sister">Sister</MenuItem>
                      <MenuItem value="Rev.">Rev.</MenuItem>
                      <MenuItem value="Capt.">Capt.</MenuItem>
                      <MenuItem value="Col.">Col.</MenuItem>
                      <MenuItem value="Maj.">Maj.</MenuItem>
                    </Select>
                    {!edit.title && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick("title")}
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                      </IconButton>
                    )}
                  </FormControl>
                  <TextField
                    label="First Name"
                    sx={{ flex: 1 }}
                    value={patientInfo.firstName}
                    onChange={handleInputChange}
                    name="firstName"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton edge="end">
                            {!edit.firstName && (
                              <EditIcon
                                sx={{ color: "transparent", fontSize: "18px" }}
                                onClick={() => handleEditClick("firstName")}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      readOnly: !edit.firstName,
                    }}
                  />
                </Box>
                <TextField
                  label="Last Name"
                  sx={{ flex: "1 1 30%" }}
                  value={patientInfo.lastName}
                  onChange={handleInputChange}
                  name="lastName"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          {!edit.lastName && (
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() => handleEditClick("lastName")}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !edit.lastName,
                  }}
                />
                <DatePicker
                  label="DOB"
                  value={patientInfo.dob ? dayjs(patientInfo.dob) : null}
                  onChange={(newValue) => {
                    const dateValue = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                    handleInputChange({
                      target: { name: "dob", value: dateValue },
                    });
                  }}
                  maxDate={dayjs()}
                  readOnly={!edit.dob}
                  slotProps={{
                    textField: {
                      sx: { flex: "1 1 30%" },
                      ...(!edit.dob && {
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton edge="end">
                                <EditIcon sx={{ color: "transparent", fontSize: "18px" }} onClick={() => handleEditClick("dob")} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }),
                    },
                  }}
                />

                <FormControl sx={{ flex: "1 1 30%", position: "relative" }}>
                  <InputLabel
                    sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                  >
                    Gender
                  </InputLabel>
                  <Select
                    label="Gender"
                    value={patientInfo.gender}
                    onChange={handleInputChange}
                    name="gender"
                    disabled={!edit.gender}
                    variant="outlined"
                    // IconComponent={() => edit.gender ? <ArrowDropDownIcon /> : null}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {!edit.gender && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick("gender")}
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                    </IconButton>
                  )}
                </FormControl>

                <TextField
                  label="Contact Number"
                  sx={{ flex: "1 1 30%" }}
                  value={patientInfo.phone}
                  onChange={handleInputChange}
                  name="contactNo"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          {!edit.contactNo && (
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() => handleEditClick("phone")}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !edit.phone,
                  }}
                />
                <TextField
                  label="Address"
                  sx={{ flex: "1 1 30%" }}
                  value={patientInfo.address}
                  onChange={handleInputChange}
                  name="address"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          {!edit.address && (
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() => handleEditClick("address")}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !edit.address,
                  }}
                />
                <FormControl sx={{ flex: "1 1 30%" }} disabled={!edit.city}>
                  <InputLabel id="city-select-label">City</InputLabel>
                  <Select
                    labelId="city-select-label"
                    label="City"
                    value={patientInfo.city || ""}
                    onChange={handleInputChange}
                    name="city"
                    endAdornment={
                      !edit.city && (
                        <InputAdornment position="end">
                          <IconButton edge="end">
                            <EditIcon sx={{ color: "transparent", fontSize: "18px" }} onClick={() => handleEditClick("city")} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {cityList.map((city) => (
                      <MenuItem key={city._id} value={city._id}>
                        {city.cityName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: "1 1 30%" }} disabled={!edit.state}>
                  <InputLabel id="state-select-label">State</InputLabel>
                  <Select
                    labelId="state-select-label"
                    label="State"
                    value={patientInfo.state || ""}
                    onChange={handleInputChange}
                    name="state"
                    endAdornment={
                      !edit.state && (
                        <InputAdornment position="end">
                          <IconButton edge="end">
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() => handleEditClick("state")}
                            />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {stateList.map((state) => (
                      <MenuItem key={state._id} value={state._id}>
                        {state.stateName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Pincode"
                  sx={{ flex: "1 1 30%" }}
                  value={patientInfo.pinCode}
                  onChange={handleInputChange}
                  name="pinCode"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          {!edit.pinCode && (
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() => handleEditClick("pinCode")}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !edit.pinCode,
                  }}
                />
              </Box>
            </Box>

            {/* Emergency Contact */}
            <Box
              sx={{
                padding: 2,
                backgroundColor: "#f5faff",
                borderRadius: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1" color="grey" marginBottom={2}>
                Emergency Contact
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <TextField
                  label="Name"
                  sx={{ flex: "1 1 30%" }}
                  value={patientInfo.emergencyContactName}
                  onChange={handleInputChange}
                  name="emergencyContactName"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          {!edit.emergencyContactName && (
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() =>
                                handleEditClick("emergencyContactName")
                              }
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !edit.emergencyContactName,
                  }}
                />
                <Box sx={{ flex: "1 1 30%", position: "relative" }}>
                  <Autocomplete
                    options={relationshipList || []}
                    getOptionLabel={(option) => option.relationshipName || ""}
                    value={relationshipList?.find(relationship => relationship.relationshipName === patientInfo.emergencyContactRelationship) || null}
                    onChange={(event, newValue) => {
                      handleInputChange({
                        target: {
                          name: "emergencyContactRelationship",
                          value: newValue ? newValue.relationshipName : ""
                        }
                      });
                    }}
                    disabled={!edit.emergencyContactRelationship}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Relationship"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          readOnly: !edit.emergencyContactRelationship,
                        }}
                      />
                    )}
                  />
                  {!edit.emergencyContactRelationship && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleEditClick("emergencyContactRelationship")
                      }
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                    </IconButton>
                  )}
                </Box>
                <TextField
                  label="Contact Number"
                  sx={{ flex: "1 1 30%" }}
                  value={patientInfo.emergencyContactNumber}
                  onChange={handleInputChange}
                  name="emergencyContactNumber"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          {!edit.emergencyContactNumber && (
                            <EditIcon
                              sx={{ color: "transparent", fontSize: "18px" }}
                              onClick={() =>
                                handleEditClick("emergencyContactNumber")
                              }
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: !edit.emergencyContactNumber,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Insurance Info */}
        <Box
          sx={{
            padding: 2,
            backgroundColor: "#f5faff",
            borderRadius: 2,
            marginBottom: 2,
          }}
        >
          <Typography variant="subtitle1" color="grey" marginBottom={2}>
            Insurance Information
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <FormControl
              sx={{ flex: "1 1 Calc(25% - 15px)", position: "relative" }}
            >
              <InputLabel
                sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
              >
                Insurance Provider
              </InputLabel>
              <Select
                label="Insurance Provider"
                value={patientInfo.insuranceProvider ?? ""}
                onChange={handleInputChange}
                name="insuranceProvider"
                disabled={!edit.insuranceProvider}
                variant="outlined"
                sx={
                  edit.insuranceProvider && patientInfo.insuranceProvider
                    ? { "& .MuiSelect-select": { pr: 4.5 } }
                    : undefined
                }
              >
                <MenuItem value="LIC Health Insurance">
                  LIC Health Insurance
                </MenuItem>
                <MenuItem value="ICICI Lombard">ICICI Lombard</MenuItem>
                <MenuItem value="HDFC ERGO">HDFC ERGO</MenuItem>
                <MenuItem value="Star Health Insurance">
                  Star Health Insurance
                </MenuItem>
                <MenuItem value="Religare Health Insurance">
                  Religare Health Insurance
                </MenuItem>
              </Select>
              {edit.insuranceProvider ? (
                <IconButton
                  size="small"
                  aria-label="Cancel insurance changes"
                  disabled={isSaving}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelInsuranceEdit}
                  sx={{
                    position: "absolute",
                    right: 36,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                  }}
                >
                  <X size={16} />
                </IconButton>
              ) : null}
              {!edit.insuranceProvider && (
                <IconButton
                  size="small"
                  onClick={() => handleEditClick("insuranceProvider")}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <EditIcon sx={{ color: "transparent", fontSize: "18px" }} />
                </IconButton>
              )}
            </FormControl>
            <TextField
              label="Policy Number"
              sx={{ flex: "1 1 Calc(25% - 15px)" }}
              value={patientInfo.policyNumber}
              onChange={handleInputChange}
              name="policyNumber"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      {!edit.policyNumber && (
                        <EditIcon
                          sx={{ color: "transparent", fontSize: "18px" }}
                          onClick={() => handleEditClick("policyNumber")}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                readOnly: !edit.policyNumber,
              }}
            />

            <TextField
              label="Coverage Details (Brief Description if necessary)"
              sx={{ flex: "1 1 Calc(50% - 8px)" }}
              value={patientInfo.coverage}
              onChange={handleInputChange}
              name="coverage"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      {!edit.coverage && (
                        <EditIcon sx={{ color: "transparent", fontSize: "18px" }} onClick={() => handleEditClick("coverage")} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                readOnly: !edit.coverage,
              }}
            />
          </Box>
        </Box>

        {/* Register Button */}
        <Box sx={{ textAlign: "center" }}>
          {Object.values(edit).some((value) => value === true) && (
            <Button
              variant="contained"
              color="primary"
              sx={{ padding: "10px 40px", width: "30%" }}
              onClick={updatePatient}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Update"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Information;
